import { baseKeymap } from 'prosemirror-commands';
import { keymap } from 'prosemirror-keymap';
import { Schema } from 'prosemirror-model';

import packages, { packageNames } from './packages';

const { CORE } = packageNames;

const _getPackages = (config) => {

	const packageList = [];

	packageList.push(packages[CORE]);

	const configPackages = Object.keys(config);

	configPackages.forEach((key) => {

		if (packages[key]) {

			packageList.push(packages[key]);

		}

	});

	return packageList;

};

const _processPackages = (packageList, options) => {

	const nodes = {}, marks = {};

	let plugins = [], toolbarButtons = [], tooltips = [];

	packageList.forEach((pkg) => {

		if (pkg.nodes) {

			const pkgNodes = pkg.nodes();

			Object.keys(pkgNodes).forEach((nodeKey) => {

				nodes[nodeKey] = pkgNodes[nodeKey];

			});

		}

		if (pkg.marks) {

			const pkgMarks = pkg.marks();

			Object.keys(pkgMarks).forEach((markKey) => {

				marks[markKey] = pkgMarks[markKey];

			});

		}

		if (pkg.plugins) {

			plugins = plugins.concat(pkg.plugins(options));

		}

		if (pkg.toolbarButtons) {

			toolbarButtons = toolbarButtons.concat(pkg.toolbarButtons());

		}

		if (pkg.tooltips) {

			tooltips = tooltips.concat(pkg.tooltips());

		}

	});

	// If mentions are enabled, ensure that the
	// mention plugin runs before other plugins
	// so that keyboard overrides take precedence
	const mentionPluginIdx = plugins.findIndex((plugin) => plugin.key === 'mentionTriggerPlugin$');

	if (mentionPluginIdx >= 0) {

		const mentionPlugin = plugins[mentionPluginIdx];

		plugins.splice(mentionPluginIdx, 1);
		plugins.splice(0, 0, mentionPlugin);

	}

	// If merge tags are enabled, ensure that the
	// merge tag plugin runs before other plugins
	// so that keyboard overrides take precedence
	const mergeTagPluginIdx = plugins.findIndex((plugin) => plugin.key === 'mergeTagTriggerPlugin$');

	if (mergeTagPluginIdx >= 0) {

		const mergeTagPlugin = plugins[mergeTagPluginIdx];

		plugins.splice(mergeTagPluginIdx, 1);
		plugins.splice(0, 0, mergeTagPlugin);

	}

	return {
		nodes,
		marks,
		plugins,
		toolbarButtons,
		tooltips,
	};

};

/**
 * Builds the schema and plugin list needed to instantiate a new Prosemirror editor
 * state as well as the UI components to render.
 *
 * @param {Object} config  The configuration object used to build the output.
 *
 * @param {Object} options                  Additional options for specific packages.
 * @param {string} options.emojiSize        The style string for the emoji width and height.
 * @param {string} options.placeholderText  The display text when there is no content.
 * @param {string} options.scope            The scope of the editor (used for links).
 * @param {string} options.scopeId          The scope ID of the editor (used for links).
 *
 * @returns {Object}  The schema, plugins, and ui components for the editor.
 *
 */
const configureEditor = (config, options = {}) => {

	const packageList = _getPackages(config);

	const {
		nodes,
		marks,
		plugins,
		toolbarButtons,
		tooltips,
	} = _processPackages(packageList, options);

	const schema = new Schema({
		nodes,
		marks,
	});

	const baseKeymapPlugin = keymap(baseKeymap);

	return {
		schema,
		plugins: [...plugins, baseKeymapPlugin],
		ui: {
			toolbarButtons,
			tooltips,
		},
	};

};

export default configureEditor;
